import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import Sidebar from "../../component/Sidebar";
import Topbar from "../../component/Topbar";
import { 
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaGlobe, FaBriefcase, 
  FaFileInvoice, FaBoxOpen, FaChevronDown, FaChevronUp, FaArrowLeft 
} from "react-icons/fa";
import Swal from "sweetalert2";


import defaultimg from"../../assets/img/default.png";

function UserDetail() {
  const { uuid } = useParams();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState({});

  const fetchUser = async () => {
    setLoadingUser(true);
    try {
      const res = await axios.get(`${baseurl}getUserByUuid/${uuid}`);
      if (res.data.data) {
        setUser(res.data.data);
        fetchOrders(res.data.data.id);
      } else Swal.fire("Error", "User not found", "error");
    } catch (err) {
      console.error("Fetch user error:", err);
      Swal.fire("Error", "Failed to fetch user", "error");
    } finally {
      setLoadingUser(false);
    }
  };

  const fetchOrders = async (userId) => {
    setLoadingOrders(true);
    try {
      const res = await axios.get(`${baseurl}getOrdersByCustomerId/${userId}`);
      if (res.data.success) setOrders(res.data.orders || []);
    } catch (err) {
      console.error("Fetch orders error:", err);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, [uuid]);

  const toggleOrder = (orderId) => {
    setExpandedOrders((prev) => ({
      ...prev,
      [orderId]: !prev[orderId],
    }));
  };

  if (loadingUser) {
    return (
      <div className="vh-100 d-flex justify-content-center align-items-center bg-light">
        <div className="spinner-grow text-primary" role="status"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <>
      <Topbar />
      <Sidebar />
      <main className="main-wrapper" style={{ backgroundColor: "#f3f4f6", minHeight: "100vh" }}>
        <div className="container-fluid p-4">
          
          {/* Header Actions */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <Link to="/all-users" className="btn btn-white shadow-sm rounded-pill px-3">
              <FaArrowLeft className="me-2" /> Back to Directory
            </Link>
            <div className="badge bg-white text-dark shadow-sm p-2 rounded-pill px-3 border">
              User UUID: <span className="text-primary fw-bold">{uuid.substring(0, 8)}...</span>
            </div>
          </div>

          <div className="row g-4">
            {/* Left Column: User Profile */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden sticky-top" style={{ top: "90px" }}>
                <div className="card-header bg-primary py-5 border-0 position-relative" 
                     style={{ background: "linear-gradient(45deg, #4e73df 0%, #224abe 100%)" }}>
                </div>
                <div className="card-body text-center position-relative" style={{ marginTop: "-60px" }}>
                  <div className="mb-3 position-relative d-inline-block">
                    <img
                      src={user.profile_image ? `${imgurl}${user.profile_image}` :  defaultimg }
                      alt={user.name}
                      className="rounded-circle border border-4 border-white shadow"
                      style={{ width: 120, height: 120, objectFit: "cover" }}
                    />
                    <span className={`position-absolute bottom-0 end-0 border border-3 border-white p-2 rounded-circle ${user.status === "active" ? "bg-success" : "bg-danger"}`}></span>
                  </div>
                  <h4 className="fw-bold mb-1">{user.name}</h4>
                  <p className="text-muted text-uppercase small fw-bold tracking-wider mb-3">
                    <FaBriefcase className="me-1" /> {user.role || "Customer"}
                  </p>
                  
                  <div className="d-flex justify-content-center gap-2 mb-4">
                    <span className="badge bg-primary-subtle text-primary border border-primary px-3 py-2 rounded-pill">
                      {user.status?.toUpperCase()}
                    </span>
                  </div>

                  <div className="text-start space-y-3">
                    <h6 className="text-uppercase text-muted small fw-bold mb-3 border-bottom pb-2">Contact Details</h6>
                    <div className="d-flex align-items-center mb-2">
                      <div className="icon-box-sm bg-info-subtle text-info me-3"><FaEnvelope /></div>
                      <div>
                        <p className="small text-muted mb-0">Email Address</p>
                        <p className="fw-medium mb-0">{user.email || "N/A"}</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center mb-2">
                      <div className="icon-box-sm bg-success-subtle text-success me-3"><FaPhone /></div>
                      <div>
                        <p className="small text-muted mb-0">Phone Number</p>
                        <p className="fw-medium mb-0">{user.phone || "N/A"}</p>
                      </div>
                    </div>
                    {user.website && (
                      <div className="d-flex align-items-center mb-2">
                        <div className="icon-box-sm bg-warning-subtle text-warning me-3"><FaGlobe /></div>
                        <div>
                          <p className="small text-muted mb-0">Website</p>
                          <a href={user.website} target="_blank" rel="noreferrer" className="text-decoration-none fw-medium">{user.website}</a>
                        </div>
                      </div>
                    )}

                    <h6 className="text-uppercase text-muted small fw-bold mt-4 mb-3 border-bottom pb-2">Business & Address</h6>
                    <div className="p-3 bg-light rounded-3 mb-3">
                        <p className="small mb-1"><strong>Company:</strong> {user.business_name || "Personal Account"}</p>
                        <p className="small mb-0"><strong>Tax ID:</strong> {user.gst_number || "No GST"}</p>
                    </div>
                    <div className="d-flex">
                        <FaMapMarkerAlt className="text-danger mt-1 me-2" />
                        <p className="small text-muted lh-sm">
                            {[user.address_line1, user.address_line2, user.city, user.state, user.postal_code, user.country]
                            .filter(Boolean).join(", ") || "No address on file"}
                        </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Order History */}
            <div className="col-lg-8">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h5 className="fw-bold m-0"><FaBoxOpen className="me-2 text-primary" /> Purchase History</h5>
                <span className="badge bg-white text-dark border shadow-sm px-3 py-2 rounded-pill">{orders.length} Total Orders</span>
              </div>

              {loadingOrders ? (
                <div className="card border-0 shadow-sm p-5 text-center rounded-4">
                  <div className="spinner-border text-primary mx-auto" role="status"></div>
                  <p className="mt-3 text-muted">Loading purchase records...</p>
                </div>
              ) : orders.length === 0 ? (
                <div className="card border-0 shadow-sm p-5 text-center rounded-4">
                  <h6 className="text-muted">No transactions found for this customer.</h6>
                </div>
              ) : (
                <div className="order-timeline">
                  {orders.map((order) => (
                    <div key={order.order_id} className="card border-0 shadow-sm rounded-4 mb-3 overflow-hidden">
                      <div className="card-body p-0">
                        {/* Order Header */}
                        <div className="p-3 d-flex flex-wrap justify-content-between align-items-center bg-white">
                          <div className="d-flex align-items-center">
                            <div className="icon-box bg-light rounded-3 me-3">
                                <FaFileInvoice className="text-primary" />
                            </div>
                            <div>
                                <h6 className="mb-0 fw-bold">#{order.order_uuid?.substring(0, 12)}</h6>
                                <small className="text-muted">{order.payment_method} • ₹{order.totals?.total_amount}</small>
                            </div>
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <span className={`badge rounded-pill px-3 py-2 ${
                                order.order_status === "Confirmed" ? "bg-success-subtle text-success" : "bg-warning-subtle text-warning"
                            }`}>
                                {order.order_status}
                            </span>
                            <button 
                              onClick={() => toggleOrder(order.order_id)}
                              className="btn btn-light btn-sm rounded-circle p-2"
                            >
                              {expandedOrders[order.order_id] ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                          </div>
                        </div>

                        {/* Expandable Content */}
                        {expandedOrders[order.order_id] && (
                          <div className="p-3 bg-light border-top animate__animated animate__fadeIn">
                            <div className="row g-3">
                                <div className="col-md-6 border-end">
                                    <p className="small fw-bold text-muted text-uppercase mb-2">Shipping To</p>
                                    <p className="small mb-0 fw-medium">{order.shipping_details?.city}, {order.shipping_details?.state}</p>
                                    <p className="small text-muted">{order.shipping_details?.address_line1}</p>
                                </div>
                                <div className="col-md-6 ps-md-4">
                                    <p className="small fw-bold text-muted text-uppercase mb-2">Order Items</p>
                                    <div className="space-y-2">
                                        {order.items.map((item, idx) => (
                                            <div key={idx} className="d-flex align-items-center justify-content-between bg-white p-2 rounded-3 border mb-2">
                                                <div className="d-flex align-items-center">
                                                    <img 
                                                        src={item.product_image ? `${imgurl}${item.product_image}` : "https://via.placeholder.com/40"} 
                                                        className="rounded me-2" 
                                                        style={{ width: 40, height: 40, objectFit: "cover" }} 
                                                        alt=""
                                                    />
                                                    <div>
                                                        <p className="mb-0 small fw-bold text-truncate" style={{maxWidth: '150px'}}>{item.product_data?.name}</p>
                                                        <small className="text-muted">Qty: {item.item_quantity}</small>
                                                    </div>
                                                </div>
                                                <span className="small fw-bold">₹{item.item_price}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <Link to={`/order-details/${order.order_id}`} className="btn btn-primary btn-sm w-100 mt-3 rounded-pill">
                                        Full Invoice Details
                                    </Link>
                                </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .bg-primary-subtle { background-color: #eef2ff !important; }
        .bg-success-subtle { background-color: #ecfdf5 !important; }
        .bg-info-subtle { background-color: #e0f2fe !important; }
        .bg-warning-subtle { background-color: #fffbeb !important; }
        
        .icon-box-sm {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
        }

        .icon-box {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
        }

        .btn-white { background: #fff; border: 1px solid #dee2e6; }
        .btn-white:hover { background: #f8f9fa; }

        .tracking-wider { letter-spacing: 0.05em; }
        .space-y-3 > * + * { margin-top: 1rem; }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate__fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </>
  );
}

export default UserDetail;